<script>
   /****************************************************************/
   /* BiPlot                                                       */
   /* --------------------                                         */
   /* shows bi-plot with PCA scores and loadings                   */
   /****************************************************************/

   import { Axes, XAxis, YAxis, Box, Segments, Points, TextLabels } from 'svelte-plots-basic/2d';
   import { PNG_WIDTH, PNG_HEIGHT, PNG_RES, MARGINS, AXIS_COLOR } from '../../../shared/plots/Defaults.js';
   import { Vector } from 'mdatools/arrays';

   import { pcares_get, pcamodel_get } from '../../../shared/wasm/dist/pca.js';


   /*****************************************/
   /* Component parameters                  */
   /*****************************************/

   // inputs
   export let params;                        // JSON with main plot parameters
   export let res;                           // JSON with result object
   export let model;                         // JSON with model object

   // saving parameters
   export let fileName = 'plot-biplot';
   export let pngWidth = PNG_WIDTH;
   export let pngHeight = PNG_HEIGHT;
   export let pngRes = PNG_RES;


   /*****************************************/
   /* Component code                        */
   /*****************************************/

   // constant plot parameters
   const showGrid = true;
   const pColor = "#2679B2";
   const tColor = "#D22C2F";
   const pColorText = "#667892";
   const tColorText = "#927866";

   /**
    * Generates axis title for given component and result object.
    * @param resobj - result object (single)
    * @param ncomp - component index (from 1)
    */
   function getAxisTitle(resobj, ncomp) {
      return `PC${ncomp} (${(resobj.stat.expvar[ncomp - 1] * 100).toFixed(1)}%)`;
   }

   // local copies of changeable plot parameters
   let comp = [1, 1];
   let plotType = 'p';
   let showLabels = 'hide';
   let axesScale = 'normal';

   // reactive expressions to monitor changes in plot parameters
   $: if (params && params.comp !== comp) comp = params.comp;
   $: if (params && params.plotType !== plotType) plotType = params.plotType;
   $: if (params && params.showLabels !== showLabels) showLabels = params.showLabels;
   $: if (params && params.axesScale !== axesScale) axesScale = params.axesScale;

   $: eigenvals = model ?  pcamodel_get(model, "eigenvals") : null;
   $: xStart = res ? Vector.zeros(res.ncols) : null;
   $: yStart = res ? Vector.zeros(res.ncols) : null;

   function getValues(a) {
      const t = pcares_get(res, 'scores', a).slice();
      const p = pcamodel_get(model, 'loadings', a);

      const nobj = t.length;
      const nvar = p.length;

      let vMin = Number.POSITIVE_INFINITY;
      let vMax = Number.NEGATIVE_INFINITY;

      const tpos = new Array(nobj);
      for (let i = 0; i < nobj; i++ ) {
         t[i] = t[i] / Math.sqrt(eigenvals[a - 1] * (nobj - 1));
         if (vMin > t[i]) vMin = t[i];
         if (vMax < t[i]) vMax = t[i];
         tpos[i] = t[i] < 0 ? 1 : 3;
      }

      const ppos = new Array(nobj);
      for (let i = 0; i < nvar; i++ ) {
         if (vMin > p[i]) vMin = p[i];
         if (vMax < p[i]) vMax = p[i];
         ppos[i] = p[i] < 0 ? 1 : 3;
      }

      const vValues = new Vector(t);
      const vEnd = new Vector(p);
      const vLabel = getAxisTitle(res, a);
      const dV = (vMax - vMin) / 16;
      const vLim = [vMin - dV, vMax + dV];
      return [vValues, vEnd, vLim, vLabel, tpos, ppos];
   }

   let compX = -1, xValues, xEnd, xLabel, limXc;
   $: if (res && comp && compX !== comp[0]) {
      compX = comp[0];
      let a, b;
      [xValues, xEnd, limXc, xLabel, a, b] = getValues(compX);
   }

   let compY = -1, yValues, yEnd, yLabel, limYc, tpos, ppos;
   $: if (res && comp && compY !== comp[1]) {
      compY = comp[1];
      [yValues, yEnd, limYc, yLabel, tpos, ppos] = getValues(compY);
   }

   let limX, limY;
   $: if (axesScale === 'equal') {
      const v = Math.max(Math.abs(limXc[0]), Math.abs(limYc[0]), limXc[1], limYc[1]) * 1.05;
      limX = [-v, v];
      limY = [-v, v];
   } else {
      limX = limXc;
      limY = limYc;
   }
</script>

{#if xValues && yValues && xEnd && yEnd}
<div class="plot-container">
<Axes
   {xLabel} {yLabel} {limX} {limY}
   {fileName} {pngWidth} {pngHeight} {pngRes}
   title="Bi-plot (scores and loadings)"
   downloadLinks="hover"
   margins={MARGINS}
>

   <!-- axis lines (x = 0 and y = 0) -->
   <Segments xStart={[limX[0]]} xEnd={[limX[1]]} yStart={[0]} yEnd={[0]} lineColor={AXIS_COLOR} />
   <Segments xStart={[0]} xEnd={[0]} yStart={[limY[0]]} yEnd={[limY[1]]} lineColor={AXIS_COLOR} />

   <!-- scores as points -->
   <Points {xValues} {yValues} borderColor={tColor} borderWidth={1.5}/>

   <!-- loadings as segments -->
   <Segments {xStart} {yStart} {xEnd} {yEnd} lineColor={pColor} />
   <Points xValues={xEnd} yValues={yEnd} marker={1} borderColor={pColor} faceColor={pColor} markerSize={0.4} />

   {#if showLabels === 'obj' || showLabels === 'both'}
   <TextLabels {xValues} {yValues} labels={res.objlabels} pos={tpos} textSize={0.75} faceColor={tColorText}/>
   {/if}
   {#if showLabels === 'var' || showLabels === 'both'}
   <TextLabels xValues={xEnd} yValues={yEnd} labels={res.varlabels} textSize={0.75} pos={ppos} faceColor={pColorText}/>
   {/if}
   <XAxis {showGrid} slot="xaxis" />
   <YAxis {showGrid} slot="yaxis" />
   <Box slot="box" />
</Axes>
</div>

{/if}

<style>
   .plot-container {
      width: 100%;
      height: calc(100% - 15px);
   }
   .msg-select {
      height: 15px;
      font-size: 12px;
      color: #aaa;
      text-align: center;
   }
</style>
